/**
 * ProjeBilgiDuzenleModal.jsx — "Proje Bilgilerini Düzenle" on the stand detail (backlog #210).
 *
 * Staff edit the project's stand type, height band and accepted height (PUT /projects/{id}, only the changed
 * fields are sent). Rules and the reasons for what is NOT editable: projeBilgiDuzenle.js.
 *
 * The stand type is a cascade over the catalog tree (GET /stand-types-classic?parentId=): one select per level,
 * pre-filled from the project's own path. The band list is the picked type's (GET /stand-heights-classic):
 * a type with no band cannot be saved. Changing the type or the band recalculates the project's required
 * documents on the backend, which can drop approvals - the window says so before the save.
 *
 * `project` is the bridged project (GET /projects/by-stand): { id, standTypeId, standHeightId, projectHeightNumber }.
 * `onSaved()` runs after a successful PUT (the page reloads the project). The form is mounted only while the
 * window is open, so every opening starts from the project's current values.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  getStandTypes, getStandTypePath, getStandHeights, pickHeightBand, updateProject,
} from "../../services/projectService";
import {
  buildProjectUpdateBody, heightToText, parseHeight, recalculatesDocuments, validateProjectEdit,
} from "./projeBilgiDuzenle";
import { StandTypeOptions } from "./StandTypeAgaclari";

const fmtBand = (b) => `${b.stand_height_start ?? "?"}–${b.stand_height_end ?? "?"} m`;

function DuzenleForm({ toggle, project, onSaved }) {
  const { t } = useTranslation("projeler");
  // levels: [{ options: [{stand_type_id, stand_type_name}], selected: id|null }] - one select per tree level
  const [levels, setLevels] = useState(null);
  const [bandsFor, setBandsFor] = useState(null);       // { typeId, list } - the bands of the type they were fetched for
  const [bandPick, setBandPick] = useState(null);       // { typeId, id } - a band picked by hand, valid for that type only
  const [heightText, setHeightText] = useState(() => heightToText(project.projectHeightNumber));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const original = useMemo(() => ({
    standTypeId: project.standTypeId ?? null,
    standHeightId: project.standHeightId ?? null,
    projectHeightNumber: project.projectHeightNumber ?? null,
  }), [project]);

  // The picked type = the deepest selected level.
  const typeId = useMemo(() => {
    const picked = (levels || []).map((l) => l.selected).filter((v) => v != null);
    return picked.length ? picked[picked.length - 1] : null;
  }, [levels]);

  // Walk the project's type path down the catalog tree: one level per step, the path's node selected in each.
  useEffect(() => {
    let ignore = false;
    async function load() {
      try {
        const path = project.standTypeId != null
          ? ((await getStandTypePath(project.standTypeId, { forceLive: true })) || [])
          : [];
        const out = [];
        let parentId = null;
        for (let i = 0; i <= path.length; i += 1) {
          const options = (await getStandTypes(parentId, { forceLive: true })) || [];
          if (options.length === 0) break;
          const selected = path[i]?.standTypeId ?? null;
          out.push({ options, selected });
          if (selected == null) break;
          parentId = selected;
        }
        if (!ignore) setLevels(out);
      } catch (e) {
        if (!ignore) { setLevels([]); setError(e.message); }
      }
    }
    load();
    return () => { ignore = true; };
  }, [project.standTypeId]);

  // The bands of the picked type.
  useEffect(() => {
    if (typeId == null) return undefined;
    let ignore = false;
    getStandHeights(typeId, { forceLive: true })
      .then((list) => { if (!ignore) setBandsFor({ typeId, list: list || [] }); })
      .catch(() => { if (!ignore) setBandsFor({ typeId, list: [] }); });
    return () => { ignore = true; };
  }, [typeId]);

  const bands = bandsFor && bandsFor.typeId === typeId ? bandsFor.list : null;   // null = still loading

  // The band in force: a hand pick for this type, else the project's own band while its type is kept, else the
  // band that fits the height (the policy of Sistem Ataması) - always visible and changeable in the select.
  const parsedHeight = parseHeight(heightText);
  const suggestion = bands && !Number.isNaN(parsedHeight) ? pickHeightBand(bands, parsedHeight).band : null;
  const manual = bandPick && bandPick.typeId === typeId ? bandPick.id : null;
  const sameType = typeId != null && Number(typeId) === Number(original.standTypeId);
  const bandId = manual ?? (sameType
    ? (original.standHeightId != null ? String(original.standHeightId) : "")
    : (suggestion ? String(suggestion.stand_height_id) : ""));

  const body = buildProjectUpdateBody({ original, typeId, bandId, heightText });
  const bandsLoading = typeId != null && bands === null;
  const problem = bandsLoading ? "loading" : validateProjectEdit({ original, typeId, bandId, bands: bands ?? [], heightText });
  const recalculates = recalculatesDocuments(body);
  const loading = levels === null;

  async function pickLevel(index, value) {
    const id = value === "" ? null : Number(value);
    // Everything below the changed level is dropped; the band is chosen again for the new type.
    const kept = (levels || []).slice(0, index + 1).map((l, i) => (i === index ? { ...l, selected: id } : l));
    setLevels(kept);
    if (id == null) return;
    try {
      const children = (await getStandTypes(id, { forceLive: true })) || [];
      if (children.length > 0) setLevels([...kept, { options: children, selected: null }]);
    } catch (e) {
      setError(e.message);
    }
  }

  async function save() {
    if (problem || busy) return;
    setBusy(true);
    setError(null);
    try {
      await updateProject(project.id, body, { forceLive: true });
      await onSaved?.();
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  }

  const problemText = {
    height: t("projeBilgiDuzenle.yukseklikGecersiz"),
    noBands: t("projeBilgiDuzenle.bantYok"),
    band: t("projeBilgiDuzenle.bantSecin"),
  }[problem];

  return (
    <Modal isOpen toggle={busy ? undefined : toggle}>
      <ModalHeader toggle={busy ? undefined : toggle}>{t("projeBilgiDuzenle.baslik")}</ModalHeader>
      <ModalBody>
        {error && <Alert color="danger">{error}</Alert>}
        {loading ? (
          <div className="text-center py-4"><Spinner color="primary" /></div>
        ) : (
          <>
            {(levels || []).map((l, i) => (
              <FormGroup key={`${i}-${l.options[0]?.stand_type_id}`}>
                <Label size="sm">{i === 0 ? t("projeBilgiDuzenle.standTuru") : t("projeBilgiDuzenle.altTip", { seviye: i + 1 })}</Label>
                <Input
                  type="select" bsSize="sm" value={l.selected ?? ""} disabled={busy}
                  onChange={(e) => pickLevel(i, e.target.value)}
                  data-testid={`tip-seviye-${i + 1}`}
                >
                  <option value="">{t("projeBilgiDuzenle.secin")}</option>
                  {/* The catalogue name AS IS: the two parallel type trees differ only by the " (n)" marker, and here
                      (an admin cascade picker) telling them apart is the point - stripping it showed two identical "Ozel Stand".
                      A mixed list (the root level) is grouped new / legacy. */}
                  <StandTypeOptions options={l.options} />
                </Input>
                {i === 0 && <div className="text-muted small mt-1">{t("projeBilgiDuzenle.agacNotu")}</div>}
              </FormGroup>
            ))}

            <FormGroup>
              <Label size="sm">{t("projeBilgiDuzenle.yukseklikBandi")}</Label>
              <Input
                type="select" bsSize="sm" value={bandId} disabled={busy || !bands || bands.length === 0}
                onChange={(e) => setBandPick({ typeId, id: e.target.value })}
                data-testid="yukseklik-bandi"
              >
                <option value="">{t("projeBilgiDuzenle.secin")}</option>
                {(bands ?? []).map((b) => (
                  <option key={b.stand_height_id} value={b.stand_height_id}>{b.stand_height_name} · {fmtBand(b)}</option>
                ))}
              </Input>
            </FormGroup>

            <FormGroup>
              <Label size="sm">{t("projeBilgiDuzenle.kabulEdilenYukseklik")}</Label>
              <Input
                type="text" inputMode="decimal" bsSize="sm" value={heightText} disabled={busy}
                onChange={(e) => setHeightText(e.target.value)}
                data-testid="kabul-yukseklik"
              />
              <div className="text-muted small mt-1">{t("projeBilgiDuzenle.yukseklikNotu")}</div>
            </FormGroup>

            {recalculates && (
              <Alert color="warning" className="small d-flex align-items-start gap-2 mb-2" data-testid="belge-uyarisi">
                <AlertTriangle size={16} className="flex-shrink-0 mt-1" />
                <div>{t("projeBilgiDuzenle.belgeUyarisi")}</div>
              </Alert>
            )}
            {problemText && (
              <div className="text-danger small" data-testid="duzenle-sorun">{problemText}</div>
            )}
            <div className="text-muted small mt-2">{t("projeBilgiDuzenle.digerAlanlar")}</div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={busy}>{t("common:iptal")}</Button>
        <Button color={recalculates ? "warning" : "primary"} onClick={save} disabled={loading || busy || Boolean(problem)} data-testid="duzenle-kaydet">
          {busy ? <Spinner size="sm" /> : (recalculates ? t("projeBilgiDuzenle.kaydetHesapla") : t("projeBilgiDuzenle.kaydet"))}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default function ProjeBilgiDuzenleModal({ isOpen, toggle, project, onSaved }) {
  if (!isOpen || !project) return null;
  return <DuzenleForm toggle={toggle} project={project} onSaved={onSaved} />;
}
